import { sha256Hex } from '../entropy/hash';
import { blockedBits } from '../entropy/estimates';
import { checkPixels, classifyRandomization, isProtected, RANDOMIZATION_TEXT, type KnownAnswer } from './randomization';
import type { Collector, Signal } from './types';
import { blocked, safe, short, signal } from './util';

const W = 300;
const H = 100;
const PANGRAM = 'Cwm fjordbank glyphs vext quiz, 😃';
const EMOJI = '🦊🫠🏳️‍🌈👩🏽‍💻🧑‍🦯🪿';

function context(width: number, height: number, readFrequently = false): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas.getContext('2d', readFrequently ? { willReadFrequently: true } : undefined);
}

/** A fixed scene that exercises font rasterization, emoji, blending, gradients and anti-aliasing. */
function renderScene(): string | null {
  const ctx = context(W, H);
  if (!ctx) return null;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.font = '15px "Times New Roman", serif';
  ctx.fillText(PANGRAM, 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.font = 'bold 17px Arial, sans-serif';
  ctx.fillText(PANGRAM, 4, 38);
  ctx.font = 'italic 13px Georgia, serif';
  ctx.fillStyle = '#333';
  ctx.fillText('Ŝƥȟîɲẍ ℌ₂ℴ ∑ ﬁ ½ ẞ Ж 漢字 עברית', 6, 56);
  ctx.font = '12px monospace';
  ctx.fillText('0O1lI|{}[] <=> != 0x7f', 8, 72);

  const gradient = ctx.createLinearGradient(0, 0, W, 0);
  gradient.addColorStop(0, '#ff0080');
  gradient.addColorStop(0.5, '#00ffff');
  gradient.addColorStop(1, '#ffff00');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, H - 14, W, 14);

  ctx.globalCompositeOperation = 'multiply';
  const circles: Array<[number, number, string]> = [
    [220, 40, 'rgba(255, 0, 255, 0.6)'],
    [245, 40, 'rgba(0, 255, 255, 0.6)'],
    [232, 62, 'rgba(255, 255, 0, 0.6)'],
  ];
  for (const [x, y, color] of circles) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, 22, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalCompositeOperation = 'difference';
  ctx.fillStyle = 'rgb(80, 120, 200)';
  ctx.fillRect(200, 10, 40, 30);

  ctx.globalCompositeOperation = 'source-over';
  ctx.shadowBlur = 6;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.strokeStyle = '#8a2be2';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.moveTo(150, 90);
  ctx.bezierCurveTo(170, 20, 260, 120, 295, 30);
  ctx.stroke();
  return ctx.canvas.toDataURL();
}

function renderEmoji(): string | null {
  const ctx = context(220, 44);
  if (!ctx) return null;
  ctx.textBaseline = 'top';
  ctx.font = '28px sans-serif';
  ctx.fillText(EMOJI, 2, 4);
  return ctx.canvas.toDataURL();
}

function blank(width: number, height: number): string | null {
  return context(width, height)?.canvas.toDataURL() ?? null;
}

const BG = [10, 20, 30, 255] as const;
const FG = [200, 100, 50, 255] as const;

/** Two solid rectangles on integer coordinates: no anti-aliasing, so every pixel is known exactly. */
function knownAnswer(): KnownAnswer {
  const size = 64;
  const ctx = context(size, size, true);
  if (!ctx) return 'unavailable';
  ctx.fillStyle = `rgb(${BG[0]}, ${BG[1]}, ${BG[2]})`;
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = `rgb(${FG[0]}, ${FG[1]}, ${FG[2]})`;
  ctx.fillRect(16, 16, 32, 32);
  const data = ctx.getImageData(0, 0, size, size).data;
  return checkPixels(data, size, (x, y) => (x >= 16 && x < 48 && y >= 16 && y < 48 ? FG : BG));
}

async function hashedSignal(
  id: string,
  label: string,
  first: string | null,
  state: ReturnType<typeof classifyRandomization>,
): Promise<Signal> {
  if (first === null) return blocked(id, label, 'Canvas 2D is disabled or was blocked');
  const hash = await sha256Hex(first);
  const value = { sha256: hash, bytes: first.length };
  const display = `${short(hash)}… (SHA-256 of ${first.length.toLocaleString()} bytes of PNG data)`;
  if (state === 'blocked') {
    return { ...signal(id, label, value, { display, bits: blockedBits(id) }), available: false, note: 'Readback blocked: blank image returned.' };
  }
  if (isProtected(state)) {
    return signal(id, label, value, { display, bits: 0, randomized: true, note: 'Randomized by your browser, so it is not a stable identifier.' });
  }
  return signal(id, label, value, { display });
}

export const canvasCollector: Collector = {
  id: 'canvas',
  title: 'Canvas',
  description: 'How your device draws text, emoji, shapes and gradients. Tiny rendering differences become a fingerprint.',
  async collect() {
    if (typeof document === 'undefined' || !context(1, 1)) {
      return [
        blocked('canvas.hash', 'Canvas rendering hash', 'Canvas 2D is disabled or blocked'),
        blocked('canvas.emoji', 'Emoji rendering hash', 'Canvas 2D is disabled or blocked'),
        signal('canvas.randomization', 'Randomization check', 'unavailable', { display: RANDOMIZATION_TEXT.unavailable }),
      ];
    }
    const tryRender = (fn: () => string | null) => {
      try {
        return fn();
      } catch {
        return null;
      }
    };
    const scene1 = tryRender(renderScene);
    const scene2 = tryRender(renderScene);
    const emoji1 = tryRender(renderEmoji);
    const emoji2 = tryRender(renderEmoji);
    let ka: KnownAnswer;
    try {
      ka = knownAnswer();
    } catch {
      ka = 'blocked'; // getImageData threw: readback is blocked
    }

    const sceneState = classifyRandomization({ first: scene1, second: scene2, blank: tryRender(() => blank(W, H)), knownAnswer: ka });
    // Noise from the known-answer test applies to every canvas readback, emoji included.
    const emojiState = classifyRandomization({ first: emoji1, second: emoji2, blank: tryRender(() => blank(220, 44)), knownAnswer: ka });

    return Promise.all([
      safe('canvas.hash', 'Canvas rendering hash', () => hashedSignal('canvas.hash', 'Canvas rendering hash', scene1, sceneState)),
      safe('canvas.emoji', 'Emoji rendering hash', () => hashedSignal('canvas.emoji', 'Emoji rendering hash', emoji1, emojiState)),
      Promise.resolve(
        signal('canvas.randomization', 'Randomization check', { state: sceneState, knownAnswer: ka, repeatMatched: scene1 === scene2 }, {
          display: RANDOMIZATION_TEXT[sceneState],
        }),
      ),
    ]);
  },
};
